import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchEnterpriseBySubdomain,
  fetchMyEnterpriseMemberships,
  type EnterprisePublicView,
  type EnterpriseMembershipView,
  type EnterpriseRole,
} from "@/services/enterpriseContextService";

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface EnterpriseContextValue {
  /** The subdomain used to boot this enterprise context. */
  subdomain: string;
  /** Public enterprise data; null while loading. */
  enterprise: EnterprisePublicView | null;
  /** True while the by-subdomain query is in-flight. */
  loading: boolean;
  /** True when the by-subdomain query returned 404 / error. */
  notFound: boolean;
  /** Current user's role in this enterprise; null if not a member or not yet loaded. */
  myRole: EnterpriseRole | null;
  /** Full membership object for the current user; null if not a member. */
  myMembership: EnterpriseMembershipView | null;
  /** True while the memberships query is in-flight. */
  membershipLoading: boolean;
  /** Whether the portal auth layer considers the user signed in. */
  isAuthenticated: boolean;
}

// ---------------------------------------------------------------------------
// Context + provider
// ---------------------------------------------------------------------------

const Ctx = createContext<EnterpriseContextValue | undefined>(undefined);

export function EnterpriseProvider({
  subdomain,
  isAuthenticated,
  children,
}: {
  subdomain: string;
  isAuthenticated: boolean;
  children: ReactNode;
}) {
  const enterpriseQ = useQuery({
    queryKey: ["enterprise-by-subdomain", subdomain],
    queryFn: () => fetchEnterpriseBySubdomain(subdomain),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const membershipsQ = useQuery({
    queryKey: ["my-enterprise-memberships"],
    queryFn: fetchMyEnterpriseMemberships,
    enabled: isAuthenticated && !!enterpriseQ.data,
    staleTime: 60 * 1000,
  });

  const myMembership =
    (membershipsQ.data ?? []).find(
      (m) => m.enterprise.id === enterpriseQ.data?.id,
    ) ?? null;

  const value: EnterpriseContextValue = {
    subdomain,
    enterprise: enterpriseQ.data ?? null,
    loading: enterpriseQ.isPending,
    notFound: enterpriseQ.isError,
    myRole: myMembership?.role ?? null,
    myMembership,
    membershipLoading: membershipsQ.isPending && isAuthenticated,
    isAuthenticated,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEnterprise(): EnterpriseContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useEnterprise must be used within EnterpriseProvider");
  }
  return ctx;
}

/**
 * Same as `useEnterprise` but returns `null` when outside an
 * `EnterpriseProvider` instead of throwing.  Use in shared components
 * (e.g. Sidebar) that render in both the main portal and enterprise trees.
 */
export function useEnterpriseOptional(): EnterpriseContextValue | null {
  return useContext(Ctx) ?? null;
}
